import { datetime, RRule } from 'rrule'
import {
  getYear,
  getMonth,
  getDate,
  parseISO,
  getHours,
  getMinutes
} from 'date-fns'
import { TimeUnit } from '@SonarCX/components/molecules/ReminderModal/helpers'

const DayEquivalence = {
  mon: 'MO',
  tue: 'TU',
  wed: 'WE',
  thu: 'TH',
  fri: 'FR',
  sat: 'SA',
  sun: 'SU'
}

const frequencyEquivalence = {
  year: 0,
  month: 1,
  week: 2,
  day: 3
}

const getDateValuesFromISO = (date: string) => {
  const dateObj = parseISO(date)
  const year = getYear(dateObj)
  const month = getMonth(dateObj) + 1 // Months are 0-indexed, so add 1
  const day = getDate(dateObj)
  const hours = getHours(dateObj)
  const minutes = getMinutes(dateObj)
  return { year, month, day, hours, minutes }
}

const getEndsOn = (date: string | null) => {
  if (!date) return null
  const { year, month, day } = getDateValuesFromISO(date)
  return datetime(year, month, day, 23, 59)
}

const generateEvents = (
  events,
  startyear,
  startmonth,
  startday,
  endyear,
  endmonth,
  endday
) => {
  const allEvents = []

  for (const event of events) {
    if (event.repeat === null) {
      allEvents.push(event)
      continue
    }

    const {
      year: startYear,
      month: startMonth,
      day: startDay,
      hours: startHours,
      minutes: startMinutes
    } = getDateValuesFromISO(event.stamp)

    const { repeat } = event
    const rule = new RRule({
      freq: frequencyEquivalence[repeat.by],
      interval: repeat.every,
      count: repeat.occurrences,
      byweekday:
        (event.repeat.by === TimeUnit.month ||
          event.repeat.by === TimeUnit.week) &&
        repeat.dow.length > 0
          ? repeat.dow.map(days => DayEquivalence[days])
          : null,
      dtstart: datetime(
        startYear,
        startMonth,
        startDay,
        startHours,
        startMinutes
      ),
      bysetpos:
        event.repeat.by === TimeUnit.month && repeat.dow.length > 0 ? 1 : null,
      until: getEndsOn(repeat.ends_on)
    })

    const dates = rule.between(
      datetime(startyear, startmonth, startday, 0),
      datetime(endyear, endmonth, endday, 23, 59)
    )

    const mappedRecurrence = dates.map(date => {
      return {
        ...event,
        stamp: date.toISOString()
      }
    })
    allEvents.push(...mappedRecurrence)
    //  Add month event if it has bysetpos
    // (this happens for example. remind me every tuesday of the month, and the start date is the second week or more, the event wont apper in the calendar, so add it)
    if (event.repeat.by === TimeUnit.month && repeat.dow.length > 0) {
      if (
        startYear === endyear &&
        startMonth === endmonth &&
        mappedRecurrence.length === 0
      )
        allEvents.push(event)
    }
  }
  return allEvents
}

export const generateRecurrentEvents = (
  events: APIReminder[],
  endDate: string
) => {
  const { year, month, day: endOfMonth } = getDateValuesFromISO(endDate)

  return generateEvents(events, year, month, 1, year, month, endOfMonth)
}

export const generateRecurrentEventsByDateRange = (
  events: APIReminder[],
  startDate: string,
  endDate: string
) => {
  const { year, month, day: endDay } = getDateValuesFromISO(endDate)
  const {
    year: nowYear,
    month: nowMonth,
    day: nowDay
  } = getDateValuesFromISO(startDate)

  return generateEvents(events, nowYear, nowMonth, nowDay, year, month, endDay)
}
